/**
 * Typed tool errors — semantic error classes that carry the block reason in their type
 * instead of as a hand-set __nextToastBlocked flag.
 *
 * Tools `throw` these; the centralised catch path (or `ToolErrors.handle(err)`) routes
 * them to __reject with the right reason and a localised user message. That means the
 * block reason is tied to the error at the point where it's detected — not smuggled
 * through a global mutable variable that's easy to forget or race with.
 *
 * Why types over strings / flags:
 *   - Refactor-safe: rename the class, TypeScript / IDE rename finds every call site.
 *   - Testable: smoke tests assert `err instanceof FileTooLargeError`, not string contents.
 *   - i18n-safe: the error carries enough context (e.g. limitBytes, actualBytes) that
 *     the message can be re-localised at the edge, not baked in at the throw site.
 *   - Composable: `ResolutionTooHighError extends InvalidInputError` — callers that
 *     want to treat all invalid-input cases the same can catch the base class.
 */
(function(){
'use strict';

// Canonical reason codes — defined FIRST so `handle()` and classes can reference them.
// Typed enum: typo'd strings (`'fle_too_large'`) fail ReferenceError at call time
// instead of silently routing as `'invalid_input'`.
var REASONS = Object.freeze({
    FILE_TOO_LARGE:      'file_too_large',
    WRONG_FILE_TYPE:     'wrong_file_type',
    RESOLUTION_TOO_HIGH: 'resolution_too_high',
    DURATION_TOO_LONG:   'duration_too_long',
    EMPTY_FILE:          'empty_file',
    FILE_LOST:           'file_lost',
    NO_AUDIO_STREAM:     'no_audio_stream',
    INVALID_INPUT:       'invalid_input',
    MEMORY_GUARD:        'memory_guard',
    BATCH_LIMIT:         'batch_limit',
    RATE_LIMIT:          'rate_limit'
});

// Base class — every user-input rejection inherits so callers can `catch (err)`
// and use `err instanceof InvalidInputError` as the broad gate.
class InvalidInputError extends Error {
    constructor(message, details){
        super(message);
        this.name = 'InvalidInputError';
        this.reason = 'invalid_input';
        this.details = details || null;
    }
}

class FileTooLargeError extends InvalidInputError {
    constructor(file, limitBytes){
        var sizeMB = Math.round((file && file.size || 0) / 1048576);
        var limitMB = Math.round((limitBytes || 0) / 1048576);
        super('File too large: ' + sizeMB + ' MB exceeds ' + limitMB + ' MB limit', {
            actualBytes: file && file.size || 0,
            limitBytes: limitBytes || 0,
            fileName: file && file.name || null,
            fileType: file && file.type || null
        });
        this.name = 'FileTooLargeError';
        this.reason = 'file_too_large';
    }
}

class InvalidFormatError extends InvalidInputError {
    constructor(file, acceptedFormats){
        super('Unsupported format: ' + ((file && file.type) || (file && file.name) || 'unknown'), {
            actualType: file && file.type || null,
            fileName: file && file.name || null,
            accepted: acceptedFormats || null
        });
        this.name = 'InvalidFormatError';
        this.reason = 'wrong_file_type';
    }
}

class ResolutionTooHighError extends InvalidInputError {
    constructor(width, height, maxWidth, maxHeight){
        super('Resolution ' + width + '×' + height + ' exceeds ' + maxWidth + '×' + maxHeight, {
            width: width, height: height, maxWidth: maxWidth, maxHeight: maxHeight
        });
        this.name = 'ResolutionTooHighError';
        this.reason = 'resolution_too_high';
    }
}

class DurationTooLongError extends InvalidInputError {
    constructor(seconds, maxSeconds){
        super('Duration ' + seconds.toFixed(1) + 's exceeds ' + maxSeconds + 's limit', {
            seconds: seconds, maxSeconds: maxSeconds
        });
        this.name = 'DurationTooLongError';
        this.reason = 'duration_too_long';
    }
}

class EmptyFileError extends InvalidInputError {
    constructor(file){
        super('File is empty or corrupt', {
            fileName: file && file.name || null,
            size: file && file.size || 0
        });
        this.name = 'EmptyFileError';
        this.reason = 'empty_file';
    }
}

// Container-level "this video has no audio stream" detection. Distinct from
// InvalidFormatError because the file IS a valid video — it just lacks the
// audio track the tool needs. Surfaced by audio-decoder.js Tier-0 (via the
// shared __detectVideoAudio mp4box / FFmpeg probe) BEFORE the WASM waterfall
// burns 10-30s decoding nothing. The `source` detail names which probe gave
// the verdict ('mp4box' / 'ffmpeg') — useful for telemetry triage.
class NoAudioStreamError extends InvalidInputError {
    constructor(file, source){
        super('Video has no audio stream', {
            fileName: file && file.name || null,
            fileType: file && file.type || null,
            source: source || 'unknown'
        });
        this.name = 'NoAudioStreamError';
        this.reason = 'no_audio_stream';
    }
}

/**
 * FileLostError — the File handle was revoked by the browser between
 * picking + reading. Mobile Yandex / iOS Safari does this on backgrounding
 * or memory pressure. The user can recover by re-picking the file.
 *
 * Three ways this surfaces from browser APIs:
 *   1. `file.slice(off, end).arrayBuffer()` rejects with NotReadableError
 *      or NotFoundError DOMException. Message varies: "The object can not
 *      be found here", "NotReadableError: The I/O read operation failed",
 *      "permission problems that have occurred after a reference to a file
 *      was acquired" (iOS Safari 18+), etc.
 *   2. FFmpeg WORKERFS mount throws when FileReaderSync.slice hits the
 *      revoked handle. Same DOMException names.
 *   3. Anything that proxies File reads (mp4box appendBuffer, IndexedDB
 *      put, Blob.arrayBuffer) bubbles the same shape.
 *
 * Detection: `name` first (spec-stable across browser updates), `message`
 * regex as fallback for vendor-specific phrasings. Construction: typed
 * DOMException so existing `if (err.name === 'NotFoundError')` catches
 * keep working AND the message carries `FILE_LOST:` prefix so legacy
 * string-match catches also fire.
 *
 * USE THIS instead of hand-rolling a regex in every reader module.
 * Reasons it lived in tool-errors.js: 4 separate copies in the codebase
 * had drifted (frame-extractor missed NotReadableError fast-path;
 * webcodecs-pipeline missed NotFoundError; mp4box-trim had NO catch and
 * users saw raw `NotReadableError` toast). One canonical detect+construct
 * pair eliminates that class.
 */
function isFileLostError(err){
    if (!err) return false;
    // name fast-path — spec-defined, stable across browser versions.
    var n = err.name || '';
    if (n === 'NotFoundError' || n === 'NotReadableError') return true;
    // SecurityError on iOS when File handle has been GC'd / revoked.
    if (n === 'SecurityError') return true;
    // InvalidStateError only when message tells us it's about a file —
    // the name itself isn't specific (also used for closed AbortControllers,
    // wrong-state IndexedDB transactions, etc.)
    var m = String(err.message || '');
    if (n === 'InvalidStateError' && /file|stream|read/i.test(m)) return true;
    // err.code — DOMException constants for legacy code-based checks.
    if (err.code === 'FILE_LOST') return true;
    // CRITICAL: only compare when BOTH sides are defined. Many DOMException
    // names (NotReadableError, SecurityError) have NO numeric code (.code
    // returns undefined), and a plain Error also has err.code === undefined.
    // Comparing `undefined === undefined` returns true → every plain Error
    // matched → isFileLostError returned true for ANY error → false FILE_LOST
    // toast on every cascade-died decoder. Caught by the etalon matrix on
    // Herd (S1 happy path stalled at 5 % because the decoder-died error
    // routed to the FILE_LOST "re-pick" branch instead of FFmpeg fallback).
    if (typeof DOMException !== 'undefined' && typeof err.code === 'number') {
        if ((DOMException.NOT_FOUND_ERR != null && err.code === DOMException.NOT_FOUND_ERR) ||
            (DOMException.NOT_READABLE_ERR != null && err.code === DOMException.NOT_READABLE_ERR)) {
            return true;
        }
    }
    // Message regex — catches every known vendor phrasing. New shapes
    // added here as we discover them in /pino telemetry.
    if (/^FILE_LOST/.test(m)) return true;
    if (/file_lost|no longer accessible|could not be read|object can ?not be found|object could not be found|permission problems that have occurred|reference to a file was acquired|I\/O read operation failed/i.test(m)) return true;
    return false;
}

/**
 * fileLostError(origMsg?) — construct a typed DOMException that any
 * downstream catch can detect via `isFileLostError(err)`. Default message
 * is the localised "FILE_LOST: …" prefix; pass `origMsg` to include the
 * underlying browser error for /pino diagnostics (the blade catch surfaces
 * the localised translation, not this internal message).
 */
function fileLostError(origMsg){
    var msg = 'FILE_LOST: The selected file is no longer accessible. Please re-select your file.';
    if (origMsg) msg += ' (' + String(origMsg).slice(0, 120) + ')';
    var e;
    try {
        e = new DOMException(msg, 'NotFoundError');
    } catch (_) {
        // Older browsers (no DOMException constructor) — fall back to Error.
        e = new Error(msg);
        e.name = 'NotFoundError';
    }
    e.reason = 'file_lost';
    e.code = 'FILE_LOST';
    return e;
}

/**
 * fileStillReadable(file) → Promise<boolean> — is the picked file itself still
 * there, whatever a big read just said? (2026-09-27)
 *
 * Chrome answers a whole-file read it cannot HOLD with the same NotReadableError
 * it uses for a moved or deleted file («The requested file could not be read,
 * typically due to permission problems…»). Measured in Chrome 154: a 2.5 GB
 * file.arrayBuffer() → NotReadableError, `new Uint8Array(2.5 GB)` → RangeError,
 * and file.slice(0, 4096).arrayBuffer() → fine. So isFileLostError() alone
 * calls a memory wall a lost file — /repair-video told a desktop user six times
 * in a row «телефон отозвал доступ, выберите файл заново» about a 1.3 GB file
 * its streaming strategies were reading without trouble.
 * Head and tail, 4 KB each: a moved/deleted file fails both; a file too big to
 * hold passes both. Never throws.
 */
async function fileStillReadable(file){
    try {
        if (!file || typeof file.slice !== 'function') return false;
        await file.slice(0, 4096).arrayBuffer();
        if ((file.size || 0) > 8192) await file.slice(file.size - 4096).arrayBuffer();
        return true;
    } catch (_) { return false; }
}

/**
 * Central routing for typed user-input errors.
 *
 * This is the SINGLE place across the whole codebase where typed errors
 * (FileTooLargeError, InvalidFormatError, ResolutionTooHighError, etc. —
 * any InvalidInputError subclass) get:
 *   1. Converted into a user-facing toast
 *   2. Tagged as tool_blocked with err.reason (not tool_error — these aren't bugs)
 *   3. Followed by the caller's cleanup (resetting UI state, unlocking buttons,
 *      hiding progress bars, etc.) — passed as a callback so each tool keeps
 *      its own shape without leaking UI state into this module
 *
 * Returns true when it handled the error (caller should return early without
 * running its own retry/fallback logic). Returns false for any other error
 * (real bugs, WASM OOMs, unknown crashes) — caller rethrows / retries.
 *
 * Duck-typing on err.name / err.code (not instanceof alone) so this works
 * even when ToolErrors is loaded in a different script realm than the
 * thrower (e.g. a service worker or a code-path that re-imports the module).
 *
 * Standard catch block for every FFmpegProcessor.process / AudioPipeline call:
 *
 *   catch (err) {
 *       if (ToolErrors.handle(err, err && err.message, () => resetUI())) return;
 *       // ... real-error handling / retry logic stays untouched
 *   }
 */
function isTyped(err){
    if (!err) return false;
    if (err instanceof InvalidInputError) return true;
    // Robust fallback when `instanceof` fails across realms
    if (err.name === 'InvalidInputError') return true;
    if (err.name === 'FileTooLargeError' || err.name === 'InvalidFormatError') return true;
    if (err.name === 'ResolutionTooHighError' || err.name === 'DurationTooLongError') return true;
    if (err.name === 'EmptyFileError') return true;
    if (err.name === 'NoAudioStreamError') return true;
    if (err.code === 'FFMPEG_MEMORY_GUARD' || err.code === 'AUDIO_TOO_LARGE') return true;
    return false;
}

function handle(err, localizedMessage, cleanup){
    if (!isTyped(err)) return false;
    var msg = localizedMessage || err.message;
    var reason = err.reason || (err.code === 'FFMPEG_MEMORY_GUARD' || err.code === 'AUDIO_TOO_LARGE' ? REASONS.MEMORY_GUARD : REASONS.INVALID_INPUT);
    try {
        if (typeof window.__reject === 'function') window.__reject(msg, reason);
        else if (typeof window.__toast === 'function') { window.__nextToastBlocked = reason; window.__toast(msg, 'error'); }
    } catch(_){}
    try { if (typeof cleanup === 'function') cleanup(); } catch(_){}
    return true;
}

window.ToolErrors = {
    InvalidInputError: InvalidInputError,
    FileTooLargeError: FileTooLargeError,
    InvalidFormatError: InvalidFormatError,
    ResolutionTooHighError: ResolutionTooHighError,
    DurationTooLongError: DurationTooLongError,
    EmptyFileError: EmptyFileError,
    NoAudioStreamError: NoAudioStreamError,
    REASONS: REASONS,
    handle: handle,
    isFileLostError: isFileLostError,
    fileLostError: fileLostError,
    fileStillReadable: fileStillReadable,
    version: '1.4.0'
};

})();
